/**
 * O que viaja dentro do `state`.
 *
 * Tipado porque o `decodeState` devolvia `any` implícito, e aí o destructuring do
 * outro lado não tinha nenhuma garantia de compilação — `redirectTo` podia vir a ser
 * qualquer coisa sem o compilador reclamar. Os campos são opcionais de propósito: o
 * `state` vem de fora, e um provedor pode devolver algo truncado ou adulterado.
 */
export interface EstadoExterno {
  customUrlRedirect?: string;
  strategy?: string;
  /** Destino que retoma o fluxo OIDC. Validado contra os domínios permitidos no `postAuth`. */
  redirectTo?: string;
  /** URL canônica do /authorize, repassada ao IdP para ele retomar. */
  returnTo?: string;
}

/**
 * Monta o `state` que atravessa a ida e volta do provedor externo (gov.br, ICP,
 * social). É o único lugar onde dá para levar contexto: a URL de retorno do provedor
 * é fixa por cidade (`/{cidade}/callback`), então nada que esteja na query da tela de
 * login sobrevive à viagem.
 *
 * `extra` existe para o fluxo OIDC. O `redirectTo` e o `returnTo` que retomam o
 * /authorize ficavam de fora, e por isso login por gov.br a partir de uma aplicação
 * OIDC caía no app padrão da cidade em vez de voltar para quem pediu o login.
 */
export function buildEncodedState(
  customUrlRedirect: string,
  strategy: string,
  extra?: Record<string, string | undefined>
) {
  const extras = Object.fromEntries(
    Object.entries(extra ?? {}).filter(([, valor]) => Boolean(valor))
  );

  const stateParams = {
    ...(customUrlRedirect ? { customUrlRedirect } : {}),
    ...(strategy ? { strategy } : {}),
    ...extras
  }
  return btoa(JSON.stringify(stateParams));
}

export function decodeState(encodedState: string): EstadoExterno {
  try {
    const state = atob(encodedState);
    const bruto: unknown = JSON.parse(state);

    // Vem de fora: só aproveita o que é string, e descarta o resto em silêncio. Um
    // `state` adulterado não deve virar valor de tipo inesperado mais adiante.
    if (!bruto || typeof bruto !== 'object') return {};

    const texto = (valor: unknown) => (typeof valor === 'string' ? valor : undefined);
    const objeto = bruto as Record<string, unknown>;

    return {
      customUrlRedirect: texto(objeto['customUrlRedirect']),
      strategy: texto(objeto['strategy']),
      redirectTo: texto(objeto['redirectTo']),
      returnTo: texto(objeto['returnTo'])
    };
  } catch (err) {
    console.log('Erro ao decodificar state: ', { err, encodedState });
    return {};
  }
}